import { TreeType } from '@platform-ui-kit/components-library'
import { HierarchyContainerNodeId, NavigationTreeMapping } from '@wpp-open/core'
import { useCallback } from 'react'
import { useTranslation } from 'react-i18next'

import { useUserCanManageHierarchy } from 'hooks/useUserCanManageHierarchy'
import { useCanManageHierarchy } from 'pages/admin/members/utils'
import { AzGroup, AzV2Group, TenantGroup } from 'types/groups/groups'
import { HierarchyTreeNode } from 'types/hierarchy/hierarchy'
import { MemberRole } from 'types/roles/memberRole'
import { RoleMember } from 'types/roles/roleMember'
import { Role, RoleSource } from 'types/roles/roles'
import { ShortTenantRole, TenantRole } from 'types/roles/tenantRole'
import { AZUser, UserDetailsShort } from 'types/users/users'

type TreeItemConfig = Omit<TreeType, 'children' | 'id' | 'title'>

interface UniqueAccounts {
  id: string
  name: string
}
interface AssignmentsDirect {
  assignmentId: string
  accountId: string
  roleId: string
}

export const mapNode = (
  node: HierarchyTreeNode,
  mapping: NavigationTreeMapping,
  childrenConfig?: TreeItemConfig,
): TreeType => {
  const { children, name, azId } = node
  const filteredChildren = children.map(childId => mapping[childId]) as HierarchyTreeNode[]

  return {
    title: name || '',
    id: azId!,
    ...(!!filteredChildren.length && {
      children: filteredChildren.map(childNode => mapNode(childNode, mapping, childrenConfig)),
    }),
    ...childrenConfig,
  }
}

export const useMapHierarchyTree = () => {
  const { t } = useTranslation()
  const { hierarchyTree } = useUserCanManageHierarchy()

  return {
    mapNavigationTree: useCallback(
      (osConfig?: TreeItemConfig): TreeType[] => {
        if (!hierarchyTree) {
          return []
        }

        const { mapping, rootId } = hierarchyTree
        const rootChildren = (mapping[HierarchyContainerNodeId]?.children || []).map(childId => mapping[childId])

        return [
          {
            title: t('os.common.navigation.os'),
            open: true,
            id: rootId,
            ...(!!rootChildren.length && {
              children: rootChildren.map(childNode => mapNode(childNode, mapping)),
            }),
            ...osConfig,
          },
        ]
      },
      [hierarchyTree, t],
    ),
  }
}

export const getGroupId = (group: TenantGroup | AzGroup) => ('id' in group ? group.id : group.uid)

export const isRoleCreatedOnRoot = (accountName?: string) => !!accountName && accountName === 'ROOT'

export const getRoleSource = (role: TenantRole): RoleSource => ({
  id: role.createdOnAccountId,
  name: role.createdOnAccountName,
})

export const getMemberRolesPayload = (email: string, roleId: string, accountId: string) => ({
  user_id: email,
  role_id: roleId,
  account_id: accountId,
})

export const getGroupRolesPayload = (groupId: string, roleId: string, accountId: string) => ({
  group_id: groupId,
  role_id: roleId,
  account_id: accountId,
})

export const mapGroupRolesPayloadToCreate = (groups: string[], rolesAssignments: TenantRole[]) => {
  return rolesAssignments
    ? groups.flatMap(groupId =>
        rolesAssignments.flatMap(role =>
          role.uniqueAccounts.map(({ id }) => getGroupRolesPayload(groupId, role.id, id)),
        ),
      )
    : []
}

export const mapMemberRolesPayloadToCreate = (
  members: (UserDetailsShort | { email: string; lastname: string; firstname: string })[],
  rolesAssignments: TenantRole[],
) => {
  return rolesAssignments
    ? members.flatMap(member =>
        rolesAssignments.flatMap(role =>
          role.uniqueAccounts.map(({ id }) => getMemberRolesPayload(member.email, role.id, id)),
        ),
      )
    : []
}

export const mapGroupRolesPayloadToDelete = (group: AzV2Group, role: ShortTenantRole) => {
  return role.uniqueAccounts.length
    ? role.uniqueAccounts.map(({ id }) => getGroupRolesPayload(group.id, role.id, id))
    : [getGroupRolesPayload(group.id, role.id, group.createdOnAccountId)]
}

const getAccountId = (account: UniqueAccounts | AssignmentsDirect) => ('id' in account ? account.id : account.accountId)

export const mapMemberRolesPayloadToDelete = (
  member: AZUser | RoleMember,
  role: MemberRole | Role,
  accountId: string,
) => {
  const roleUniqueAccounts = 'uniqueAccounts' in role ? role.uniqueAccounts : null
  const memberAssignmentsDirect = 'assignmentsDirect' in member ? member.assignmentsDirect : null

  const firstDefinedAccounts = [roleUniqueAccounts, memberAssignmentsDirect].find(
    accounts => !!accounts && accounts.length,
  )

  return firstDefinedAccounts
    ? firstDefinedAccounts.map(account => getMemberRolesPayload(member.email, role.id, getAccountId(account)))
    : [getMemberRolesPayload(member.email, role.id, accountId)]
}

export const mapMembersRolesPayloadToDelete = (members: RoleMember[], role: MemberRole | Role, accountId: string) => {
  return members
    .map(member => {
      const memberAssignmentsDirect = 'assignmentsDirect' in member ? member.assignmentsDirect : null

      const accounts = [memberAssignmentsDirect].find(accounts => !!accounts && accounts.length)

      return accounts
        ? accounts.map(account => getMemberRolesPayload(member.email, role.id, getAccountId(account)))
        : getMemberRolesPayload(member.email, role.id, accountId)
    })
    .flat()
}

export const sortNavigationTree = (node: TreeType): TreeType => {
  if (!node.children || !node.children.length) {
    return node
  }
  return {
    ...node,
    children: node.children
      .sort(({ title }, { title: secondTitle }) => title?.localeCompare(secondTitle))
      .map(sortNavigationTree),
  }
}

export const useApplyPermissionsOnNavigationTree = () => {
  const { handler: canManage, isLoading } = useCanManageHierarchy()

  return {
    applyPermissions: useCallback(
      (prop: keyof TreeType = 'isNotSelectable') => {
        const applyPermissions = (node: TreeType): TreeType => {
          if (!node.children || !node.children.length) {
            return {
              [prop]: !canManage(String(node.id)),
              ...(!canManage(String(node.id)) && { iconStart: { icon: 'wpp-icon-lock-on', name: '' } }),
              ...node,
            }
          }

          return {
            ...node,
            [prop]: !canManage(String(node.id)),
            ...(!canManage(String(node.id)) && { iconStart: { icon: 'wpp-icon-lock-on', name: '' } }),
            children: node.children.map(applyPermissions),
          }
        }
        return applyPermissions
      },
      [canManage],
    ),
    isLoading,
  }
}
